import { Error } from '@app/core/models';
import { isNullish } from '@app/core/utils/types';
import { ParcelDimensions } from '@app/ecsomag/models';
import { ParcelDimensionKey, parcelDimensionKeys, ShippingInfo } from './prices';
import { isShippingCountry, getPackageSizeByName, getExtraServiceByAdditionalDataKey } from './prices.utils';
import { ValidationErrors, Validators } from '@app/core/utils/validators';
import { FirstMileType } from '../ecsomag/models/order';

export const ParcelValidatorsFactory = (shippingInfo: ShippingInfo) =>
	class ParcelValidators {
		private static shippingInfo: ShippingInfo = shippingInfo;

		static updateShippingInfo(shippingInfo: ShippingInfo) {
			ParcelValidators.shippingInfo = shippingInfo;
		}

		static destinationCountry = (destinationCountry: any): ValidationErrors => {
			const errors: ValidationErrors = [];

			if (!isShippingCountry(destinationCountry)) {
				errors.push({
					code: 'destinationCountry',
				});
			}

			return errors;
		};

		static dimension =
			(dimensionKey: ParcelDimensionKey, firstMileType: FirstMileType) =>
			(dimensionValue: number): ValidationErrors => {
				const errors: ValidationErrors = [];

				const dimensionConstraints = ParcelValidators.shippingInfo.constraints[firstMileType].dimension;

				if (dimensionConstraints[dimensionKey]) {
					const { min, max } = dimensionConstraints[dimensionKey];
					errors.push(
						...Validators.execute([
							Validators._required({ code: `dimension.${dimensionKey}.required` }),
							Validators._number({ code: `dimension.${dimensionKey}.number` }),
							Validators._integer({ code: `dimension.${dimensionKey}.integer` }),
							Validators._min(min, { code: `dimension.${dimensionKey}.min` }),
							Validators._max(max, { code: `dimension.${dimensionKey}.max` }),
						])(dimensionValue)
					);
				}

				return errors;
			};

		static dimensions =
			(firstMileType: FirstMileType) =>
			(dimensions: any): ValidationErrors => {
				return parcelDimensionKeys
					.map(dimensionKey => {
						const dimensionValue = dimensions?.[dimensionKey];
						return ParcelValidators.dimension(dimensionKey, firstMileType)(dimensionValue);
					})
					.reduce((acc, errors) => [...acc, ...errors], [] as ValidationErrors);
			};

		static size =
			(firstMileType: FirstMileType) =>
			(dimensions: any): ValidationErrors => {
				const errors: ValidationErrors = [];

				errors.push(...ParcelValidators.dimensions(firstMileType)(dimensions));

				if (errors.length > 0) {
					return [];
				}

				const circumferenceConstraints = ParcelValidators.shippingInfo.constraints[firstMileType].circumference;

				const { width, length, height } = dimensions as ParcelDimensions;
				const orderedLengths = [width, length, height].sort((a: number, b: number) => a - b);
				const sizeCircumference = orderedLengths[2] + 2 * (orderedLengths[0] + orderedLengths[1]);

				if (sizeCircumference > circumferenceConstraints.max) {
					errors.push({
						code: `size:oversizedPackage`,
						data: { max: circumferenceConstraints.max, actual: sizeCircumference, firstMileType },
					});
				}
				if (sizeCircumference < circumferenceConstraints.min) {
					errors.push({
						code: `size:undersizedPackage`,
						data: { min: circumferenceConstraints.min, actual: sizeCircumference, firstMileType },
					});
				}

				return errors;
			};

		static codAmount = (codAmount: any): ValidationErrors => {
			return Validators.compose([
				value => {
					const errors: Error[] = [];

					const extraServiceCOD = getExtraServiceByAdditionalDataKey(ParcelValidators.shippingInfo, 'cod');
					if (extraServiceCOD && extraServiceCOD.priceType === 'tiered') {
						const { min, max } = extraServiceCOD.price.reduce(
							(acc, tier) => {
								acc.min = Math.min(acc.min, tier.min);
								acc.max = Math.max(acc.max, tier.max);
								return acc;
							},
							{ min: +Infinity, max: -Infinity }
						);

						errors.push(...Validators._min(min, { code: `codAmount.min` })(value), ...Validators._max(max, { code: `codAmount.max` })(value));
					}

					return errors;
				},
				value => {
					const errors: Error[] = [];

					if (value % 5 !== 0) {
						errors.push({
							code: `codAmount.notRounded`,
						});
					}

					return errors;
				},
			])(codAmount);
		};
	};

export const validateDestinationCountry = (
	shippingInfo: ShippingInfo,
	{
		destinationCountry,
	}: {
		destinationCountry: any;
	}
): Error[] => {
	const errors: Error[] = [];

	if (!isShippingCountry(destinationCountry)) {
		errors.push({
			code: 'invalid',
		});
	}

	return errors;
};

export const validateDimension = (
	shippingInfo: ShippingInfo,
	{
		dimensionKey,
		dimensionValue,
		firstMileType,
	}: {
		dimensionKey: ParcelDimensionKey;
		dimensionValue: any;
		firstMileType: FirstMileType;
	}
): Error[] => {
	const errors: Error[] = [];

	const dimensionConstraints = shippingInfo.constraints[firstMileType].dimension;

	if (isNullish(dimensionValue)) {
		errors.push({
			code: `${dimensionKey}:required`,
		});
	} else if (typeof dimensionValue !== 'number') {
		errors.push({
			code: `${dimensionKey}:invalid`,
		});
	} else if (dimensionConstraints[dimensionKey]) {
		const { min, max } = dimensionConstraints[dimensionKey];
		const actual = dimensionValue;
		if (actual > max) {
			errors.push({
				code: `${dimensionKey}:greaterThanMax`,
				data: { max, actual },
			});
		}
		if (actual < min) {
			errors.push({
				code: `${dimensionKey}:lessThanMin`,
				data: { min, actual },
			});
		}
	}

	return errors;
};

export const validateDimensions = (
	shippingInfo: ShippingInfo,
	{
		dimensions,
		firstMileType,
	}: {
		dimensions: any;
		firstMileType: FirstMileType;
	}
): Error[] => {
	const errors: Error[] = [];

	parcelDimensionKeys.forEach(dimensionKey => {
		const dimensionValue = dimensions?.[dimensionKey];
		errors.push(...validateDimension(shippingInfo, { dimensionKey, dimensionValue, firstMileType }));
	});

	return errors;
};

export const validateSize = (
	shippingInfo: ShippingInfo,
	{
		dimensions,
		firstMileType,
	}: {
		dimensions: any;
		firstMileType: FirstMileType;
	}
): Error[] => {
	const errors: Error[] = validateDimensions(shippingInfo, { dimensions, firstMileType });

	console.log('validateSize', errors);

	if (errors.length > 0) {
		return errors;
	}

	const packageSizeXL = getPackageSizeByName(shippingInfo, 'XL');

	console.log('validateSize', packageSizeXL);

	if (packageSizeXL) {
		const { width, length, height } = dimensions as ParcelDimensions;
		const orderedLengths = [width, length, height].sort((a: number, b: number) => a - b);
		const sizeCircumference = orderedLengths[2] + 2 * (orderedLengths[0] + orderedLengths[1]);

		if (sizeCircumference > packageSizeXL.sizeLimit) {
			errors.push({
				code: `size:oversizedPackage`,
				data: { max: packageSizeXL.sizeLimit, actual: sizeCircumference },
			});
		}
	} else {
		errors.push({
			code: `size:noPackageInfo`,
		});
	}

	return errors;
};

// export const validateCODAmount = (
// 	shippingInfo: ShippingInfo,
// 	{
// 		codAmount,
// 	}: {
// 		codAmount: any;
// 	}
// ): Error[] => {
// 	const errors: Error[] = [];

// 	if (isNullish(codAmount) || codAmount.length === 0) {
// 		errors.push({
// 			code: `amount:required`
// 		});
// 	} else if (typeof (codAmount) !== 'number') {
// 		errors.push({
// 			code: `amount:invalid`
// 		});
// 	} else {
// 		const extraServiceCOD = getExtraServiceByAdditionalDataKey(shippingInfo, 'cod');
// 		if (extraServiceCOD && extraServiceCOD.priceType === 'tiered') {
// 			const { min, max } = extraServiceCOD.price.reduce(
// 				(acc, tier) => {
// 					acc.min = Math.min(acc.min, tier.min);
// 					acc.max = Math.max(acc.max, tier.max);
// 					return acc;
// 				},
// 				{ min: +Infinity, max: -Infinity }
// 			);

// 			const actual = codAmount;
// 			if (actual > max) {
// 				errors.push({
// 					code: `amount:greaterThanMax`, data: { max, actual }
// 				});
// 			}
// 			if (actual < min) {
// 				errors.push({
// 					code: `amount:lessThanMin`, data: { min, actual }
// 				});
// 			}
// 		}

// 		if (codAmount % 5 !== 0) {
// 			errors.push({
// 				code: `amount:notRounded`
// 			});
// 		}
// 	}

// 	return errors;
// };

export const validateBankAccountNumber = (bankAccountNumber: string): Error[] => {
	const errors: Error[] = [];

	if (isNullish(bankAccountNumber) || bankAccountNumber.length === 0) {
		errors.push({
			code: `bankAccountNumber:required`,
		});
	} else if (!/^\d{8}[- ]?\d{8}(?:[- ]?\d{8})?$/.test(bankAccountNumber)) {
		errors.push({
			code: `bankAccountNumber:invalidFormat`,
		});
	}

	return errors;
};

export const validateBankAccountHolder = (bankAccountHolder: string): Error[] => {
	const errors: Error[] = [];

	if (isNullish(bankAccountHolder) || bankAccountHolder.length === 0) {
		errors.push({
			code: `bankAccountHolder:required`,
		});
	}

	return errors;
};

export const validateBankAccount = ({ bankAccountNumber, bankAccountHolder }: { bankAccountNumber: string; bankAccountHolder: string }): Error[] => {
	const errors: Error[] = [];

	errors.push(...validateBankAccountNumber(bankAccountNumber), ...validateBankAccountHolder(bankAccountHolder));

	return errors;
};

export const validateRequired =
	({
		code = 'required',
	}: {
		code?: string;
	} = {}) =>
	(value: any): Error[] => {
		const errors: Error[] = [];

		if (isNullish(value) || value.length === 0) {
			errors.push({
				code,
			});
		}

		return errors;
	};

export const validatePattern = (
	pattern: string | RegExp,
	{
		code = 'invalid',
	}: {
		code?: string;
	} = {}
) => {
	let regex: RegExp;
	if (typeof pattern === 'string') {
		let regexStr: string = '';
		if (pattern.charAt(0) !== '^') regexStr += '^';
		regexStr += pattern;
		if (pattern.charAt(pattern.length - 1) !== '$') regexStr += '$';
		regex = new RegExp(regexStr);
	} else {
		regex = pattern;
	}

	return (value: string): Error[] => {
		const errors: Error[] = [];

		if (!regex.test(value)) {
			errors.push({
				code: `invalid`,
			});
		}

		return errors;
	};
};

export const validatePhoneNumber = (value: string): Error[] => {
	return validatePattern(/^\+36\d{9}$/)(value).map(error => ({ ...error, code: 'invalidPhone' }));
};

// export const validateDeliveryAddress = (deliveryAddress: any): Error[] => {
// 	const errors: Error[] = [];

// 	if (!isDeliveryAddress(deliveryAddress)) {
// 		errors.push({
// 			code: `deliveryAddress:invalid`,
// 		});
// 		// } else {
// 		// 	console.log('isDeliveryAddress', deliveryAddress)
// 		// 	if (deliveryAddress.type) {
// 		// 		switch (deliveryAddress.type) {
// 		// 			case 'home': {
// 		// 				const props = ['name', 'mobile', 'zipCode', 'city', 'street', 'houseNumber'] as (keyof HomeDeliveryRecipient)[];
// 		// 				props.forEach(prop => {
// 		// 					errors.push(
// 		// 						...validateRequired(deliveryAddress[prop]),
// 		// 					)
// 		// 				});
// 		// 			}
// 		// 			case 'parcelShop': {
// 		// 				const props = ['name', 'mobile', 'parcelShopId'] as (keyof ParcelShopRecipient)[];
// 		// 				return props.every(prop => (prop in deliveryAddress) && deliveryAddress[prop]);
// 		// 			}
// 		// 		}
// 		// 	}
// 	}

// 	return errors;
// };

// export const validateSelectedExtraService = (
// 	shippingInfo: ShippingInfo,
// 	{
// 		selectedExtraService,
// 	}: {
// 		selectedExtraService: SelectedExtraService;
// 	}
// ): Error[] => {
// 	const errors: Error[] = [];

// 	if (selectedExtraService.data?.['cod']) {
// 		errors.push(
// 			...validateCODAmount(shippingInfo, { codAmount: selectedExtraService.data['cod'].amount }),
// 			...validateBankAccount({
// 				bankAccountNumber: selectedExtraService.data['cod'].bankAccountNumber,
// 				bankAccountHolder: selectedExtraService.data['cod'].bankAccountHolder,
// 			})
// 		);
// 	}

// 	return errors;
// };

// export const validateSelectedExtraServiceForCalculation = (
// 	shippingInfo: ShippingInfo,
// 	{
// 		selectedExtraService,
// 	}: {
// 		selectedExtraService: SelectedExtraService;
// 	}
// ): Error[] => {
// 	const errors: Error[] = [];

// 	if (selectedExtraService.data?.['cod']) {
// 		errors.push(
// 			...validateCODAmount(shippingInfo, { codAmount: selectedExtraService.data['cod'].amount })
// 		);
// 	}

// 	return errors;
// };

// export const validateSelectedExtraServices = (
// 	shippingInfo: ShippingInfo,
// 	{
// 		selectedExtraServices,
// 	}: {
// 		selectedExtraServices: SelectedExtraService[];
// 	}
// ): Error[] => {
// 	const errors: Error[] = [];

// 	selectedExtraServices.forEach(selectedExtraService => {
// 		errors.push(...validateSelectedExtraService(shippingInfo, { selectedExtraService }));
// 	});

// 	return errors;
// };

// export const validateSelectedExtraServicesForCalculation = (
// 	shippingInfo: ShippingInfo,
// 	{
// 		selectedExtraServices,
// 	}: {
// 		selectedExtraServices: SelectedExtraService[];
// 	}
// ): Error[] => {
// 	const errors: Error[] = [];

// 	selectedExtraServices.forEach(selectedExtraService => {
// 		errors.push(...validateSelectedExtraServiceForCalculation(shippingInfo, { selectedExtraService }));
// 	});

// 	return errors;
// };
